import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { choiceBoardNotifications } from '@/lib/choiceBoard-notifications';

const COOLDOWN_MS = 30 * 60 * 1000;
const MAX_NOTIFICATIONS = 3;

// Module-level map persists across re-mounts for the lifetime of the page
const triggeredBoards = new Map<string | number, number>();

interface NearbyBoard {
  id: string | number;
  title: string;
  distance: number;
  locationRadius: number;
  locationAddress: string | null;
}

function handleNearbyBoards(boards: NearbyBoard[]): void {
  const now = Date.now();
  let triggered = 0;

  for (const board of boards) {
    if (triggered >= MAX_NOTIFICATIONS) break;

    const lastTriggered = triggeredBoards.get(board.id);
    if (lastTriggered && now - lastTriggered < COOLDOWN_MS) {
      console.log(`[NearbyBoards] Cooldown active for board ${board.id} — skipping`);
      continue;
    }

    // Route through the existing choice board notification manager so all notifications
    // share the same permission check, creation logic, and future behaviour changes.
    choiceBoardNotifications.sendNearbyAlert(board.id, board.title);
    triggeredBoards.set(board.id, now);
    triggered++;
  }

  console.log(`[NearbyBoards] Nearby boards triggered: ${triggered}`);
}

async function checkNearbyBoards(): Promise<void> {
  if (!navigator.geolocation) return;

  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude: lat, longitude: lng } = position.coords;
          const response = await fetchWithAuth('/api/location/nearby', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lat, lng }),
          });

          if (!response.ok) {
            resolve();
            return;
          }

          const data = await response.json();
          if (Array.isArray(data.nearbyBoards) && data.nearbyBoards.length > 0) {
            handleNearbyBoards(data.nearbyBoards);
          }
        } catch {
          // Silent failure — never block UI
        }
        resolve();
      },
      () => resolve(), // Permission denied or timeout — silently skip
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  });
}

export function useNearbyBoards(): void {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    // Check on mount
    checkNearbyBoards();

    // Check whenever the app returns to the foreground
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkNearbyBoards();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);
}
